import YoutubeVideo from '@components/common/YoutubeVideo';
import ProductSummaryBundle from '@components/Skeleton/ProductSummaryBundle';
import { ProductActivityType, ProductModel, ProductStatus } from '@models/product';
import { CalendarCartLine, isCalendarOptionProduct } from '@utils/product-option-date';
import { cdnImageUrl } from '@utils/image';
import VisibilitySensor from '@zelty/react-visibility-sensor';
import { OverlayScrollbars } from 'overlayscrollbars';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useCallback, useRef, useState } from 'react';
import { ClassCaution, ClassCautionBeforeApply } from './ClassCaution';
import { ClassIntroduction } from './ClassIntroduction';
import { ClassPreparations } from './ClassPreparations';
import { ClassPreview } from './ClassPreview';
import { ClassRecommendation } from './ClassRecommendation';
import { CommerceDetailInformation } from './CommerceDetailInformation';
import { CoupangProducts } from './CoupangProducts';
import { DetailBodyImages } from './DetailBodyImages';
import { DividerBold } from './Divider';
import { ProductActionButtons } from './ProductActionButtons';
import { ProductDetailFooter } from './ProductDetailFooter';
import { ProductDetailImages } from './ProductDetailImages';
import { ProductInformation } from './ProductInformation';
import { ProductCalendarOptionSection } from './ProductCalendarOptionSection';
import { ProductCalendarCartFooter } from './ProductCalendarCartFooter';
import { ProductPartnerSummary } from './ProductPartnerSummary';
import { ProductReviewSummary } from './ProductReviewSummary';
import { ProductStatistics } from './ProductStatistics';
import { ProductSummary } from './ProductSummary';
import { ProductRefundPolicy } from './RefundPolicy';
import { ShippingInfo } from './ShippingInfo';
import { TabBar } from './Tabbar';

interface ProductDetailContentsViewProps {
  data: ProductModel;
  setHeaderOnImage?: (value: boolean) => void;
  scrollFocusOn?: string;
  setScrollFocusOn?: (value: string) => void;
  setShowRequestChat?: (value: boolean) => void;
  setShowRequestReopenAlarm?: (value: boolean) => void;
  setShowCautions?: (value: boolean) => void;
  setShowOptionModal?: (value: boolean) => void;
  canUseButton?: boolean;
  setCanUseButton?: (value: boolean) => void;
  isLoading?: boolean;
  onRefetch?: () => void;
  myFavorite?: any;
  authStore?: any;
  appSocket?: any;
  loginModal?: any;
  share?: any;
  checkLogin?: any;
  payment?: any;
  isNativeApp?: boolean;
  okCancelDialog?: any;
  sellerUser?: any;
  chatRoom?: any;
  appNavigation?: any;
  appliedMembership?: boolean;
  scrollTopRef?: React.RefObject<any>;
  productDetailInfoRef?: React.RefObject<any>;
  productEtcInfoRef?: React.RefObject<any>;
  openInNativeApp?: (path: string) => void;
  hideRecommendProducts?: boolean;
  isCalendarOptionProduct?: boolean;
  calendarCartCount?: number;
  calendarCartTotalFee?: number;
  onAddCalendarCartLine?: (line: CalendarCartLine) => void;
  onPurchaseCalendarCart?: () => void;
  onOpenCalendarCartSheet?: () => void;
}

export const ProductDetailContentsView: React.FC<ProductDetailContentsViewProps> = ({
  data,
  setHeaderOnImage,
  scrollFocusOn,
  setScrollFocusOn,
  setShowCautions,
  setShowOptionModal,
  setShowRequestChat,
  canUseButton,
  setCanUseButton,
  isLoading,
  onRefetch,
  myFavorite,
  authStore,
  appSocket,
  loginModal,
  share,
  appliedMembership,
  scrollTopRef,
  productDetailInfoRef,
  productEtcInfoRef,
  isCalendarOptionProduct: isCalendarOptionProductProp,
  calendarCartCount,
  calendarCartTotalFee,
  onAddCalendarCartLine,
  onPurchaseCalendarCart,
  onOpenCalendarCartSheet,
}) => {
  const templateDetailImage = data?.details?.detailImageTemp || data?.details?.detailImage;
  const showCalendarOptionSection =
    isCalendarOptionProductProp ??
    (data?.saleType === 'commerce' &&
      isCalendarOptionProduct(data.productOptions, { forceOptionModal: data.forceOptionModal }));

  const calendarOptionRef = useRef<HTMLDivElement>(null);
  const [calendarFocusToken, setCalendarFocusToken] = useState(0);

  const scrollToCalendarOption = useCallback(() => {
    setCalendarFocusToken((token) => token + 1);
    requestAnimationFrame(() => {
      calendarOptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <>
      <OverlayScrollbarsComponent
        defer
        options={{
          scrollbars: { autoHide: 'scroll' },
          overflow: {
            x: 'hidden',
          },
        }}
        style={{
          backgroundColor: '#ffffff',
          marginTop: 0,
          position: 'relative',
          height: 'calc(100% - 84px)',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
        events={{
          scroll: (instance: OverlayScrollbars, event: any) => {
            const scrollTop = event.target?.scrollTop;
            if (instance) {
              if (scrollTop > 505) {
                setHeaderOnImage?.(false);
              } else {
                setHeaderOnImage?.(true);
              }
            }
          },
        }}>
        <div className="class-detail-container" style={{ paddingBottom: '40px' }}>
          <div ref={scrollTopRef}></div>
          <ProductDetailImages
            appOnly={data?.appOnly}
            appliedMembership={!!appliedMembership}
            closeText={data?.closeText}
            imageUrls={data?.detailImagesTemp || data?.detailImages || []}
            closed={data?.status === ProductStatus.CLOSE || data?.soldout}
            impressions={data?.impressions}></ProductDetailImages>
          {isLoading && <ProductSummaryBundle />}
          {data && <ProductSummary data={data} closed={data?.status === ProductStatus.CLOSE || data?.soldout} />}
          <ProductActionButtons
            productId={data?._id}
            productTitle={data?.title}
            favoritedCount={data?.favoritedCount}
            isFavorited={data?._id ? myFavorite?.isFavoriteProduct(data._id) : false}
            onShare={() => {
              share?.({ type: 'product', title: data?.title, id: data?._id });
              appSocket?.sendEvent({
                type: 'click',
                data: {
                  userId: authStore?.user?.id,
                  path: document.location.pathname,
                  actionData: {
                    from: document.location.pathname,
                    actionType: 'share',
                    productId: data?._id,
                    id: 'product-share-summary',
                  },
                },
              });
            }}
            onFavoriteToggle={() => {
              if (!authStore?.user) {
                loginModal?.open();
                return;
              }

              if (data?._id && myFavorite?.isFavoriteProduct(data?._id)) {
                myFavorite?.removeProductFromFavorites(data?._id, {
                  onSuccess: () => {
                    onRefetch?.();
                  },
                });
                appSocket?.sendEvent({
                  type: 'click',
                  data: {
                    userId: authStore?.user?.id,
                    path: document.location.pathname,
                    actionData: {
                      from: document.location.pathname,
                      actionType: 'favorite-remove',
                      productId: data?._id,
                      id: 'product-favorite',
                    },
                  },
                });
              } else if (data?._id) {
                myFavorite?.addProductToFavorites(
                  { productId: data?._id, saleType: data?.saleType },
                  {
                    onSuccess: () => {
                      onRefetch?.();
                    },
                  },
                );
                appSocket?.sendEvent({
                  type: 'click',
                  data: {
                    userId: authStore?.user?.id,
                    path: document.location.pathname,
                    actionData: {
                      from: document.location.pathname,
                      actionType: 'favorite-add',
                      productId: data?._id,
                      id: 'product-favorite',
                    },
                  },
                });
              }
            }}
            canUseButton={canUseButton}
            setCanUseButton={setCanUseButton}
          />
          <DividerBold />
          {data && <ProductStatistics data={data} />}
          {data && <ProductInformation data={data} />}
          {showCalendarOptionSection && onAddCalendarCartLine && (
            <div ref={calendarOptionRef}>
              <ProductCalendarOptionSection
                product={data}
                onAddToCart={onAddCalendarCartLine}
                focusToken={calendarFocusToken}
              />
            </div>
          )}
          <DividerBold />
          {data && <ProductReviewSummary data={data} />}
          {data && <ProductPartnerSummary data={data} />}
          {data && <DividerBold />}
          <TabBar
            saleType={data?.saleType}
            scrollFocusOn={scrollFocusOn || 'detail'}
            onClickDetail={() => {
              if (productDetailInfoRef?.current) {
                productDetailInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            onClickEtc={() => {
              if (productEtcInfoRef?.current) {
                productEtcInfoRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'start',
                });
              }
            }}
          />
          <div id="product-detail-area" ref={productDetailInfoRef}>
            {data && data.saleType === 'class' && <ClassCaution />}
            {data && data.details?.youtubeId && <YoutubeVideo youtubeId={data.details.youtubeId} />}
            {data &&
              (data.detailType === 'html' ||
                (!data.detailType && data.saleType === 'commerce' && data?.detailHtml)) && (
                <CommerceDetailInformation detailHtml={data?.detailHtml} />
              )}
            {data && (data.detailType === 'template' || (!data.detailType && data.saleType === 'class')) && (
              <>
                {!data.details?.disableDetailImage && templateDetailImage && (
                  <div style={{ width: '100%' }}>
                    <img
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'cover',
                      }}
                      src={cdnImageUrl(templateDetailImage)}
                      alt="template-detail"
                    />
                  </div>
                )}
                {!data.details?.disableClassIntroduction && <ClassIntroduction data={data} />}
                {!data.details?.disableRecommendText && <ClassRecommendation data={data} />}
                {!data.details?.disableConcepts && <ClassPreview data={data} />}
                {!data.details?.disablePreparationText && <ClassPreparations data={data} />}
              </>
            )}
            {data && data.detailType === 'images' && (
              <DetailBodyImages images={data.detailBodyImagesTemp || data.detailBodyImages || []} />
            )}
            <DividerBold style={{ marginTop: '20px' }} />
          </div>
          <VisibilitySensor
            partialVisibility
            onChange={(isVisible: boolean) => {
              setScrollFocusOn?.(isVisible ? 'etc' : 'detail');
            }}>
            <div id="product-etc-area" ref={productEtcInfoRef}>
              {data && data.saleType === 'commerce' && data.activityType === ProductActivityType.DELIVERY && (
                <ShippingInfo data={data} />
              )}
              {data && data.saleType === 'commerce' && <DividerBold />}
              {data && <ProductRefundPolicy data={data} />}
              {data && data.saleType === 'class' && <DividerBold style={{ paddingTop: '20px' }} />}
              {data && data.saleType === 'class' && (
                <ClassCautionBeforeApply
                  onClick={() => {
                    setShowCautions?.(true);
                  }}
                />
              )}
              {data && data.saleType === 'commerce' && (
                <div style={{ padding: '20px' }}>
                  <button
                    className="link-button"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid var(--Neutral-30, #E8EAED)',
                      background: 'var(--White-100, #FFF)',
                      color: 'var(--Neutral-90, #424242)',
                      textAlign: 'center',
                      fontFamily: 'Pretendard',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      height: '44px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                    }}>
                    판매자 문의
                  </button>
                </div>
              )}
              {data &&
                data.coupangProduct &&
                data.coupangProduct.products &&
                data.coupangProduct.products.length > 0 && <DividerBold />}
              {data &&
                data.coupangProduct &&
                data.coupangProduct.products &&
                data.coupangProduct.products.length > 0 && <CoupangProducts data={data} />}
            </div>
          </VisibilitySensor>
        </div>
      </OverlayScrollbarsComponent>
      {data && showCalendarOptionSection && (calendarCartCount || 0) > 0 ? (
        <ProductCalendarCartFooter
          count={calendarCartCount || 0}
          totalFee={calendarCartTotalFee || 0}
          onExpand={() => onOpenCalendarCartSheet?.()}
          onPurchase={() => onPurchaseCalendarCart?.()}
        />
      ) : (
        data && (
          <ProductDetailFooter
            data={data}
            onClick={() => {
              if (data?.saleType === 'commerce') {
                if (showCalendarOptionSection) {
                  if ((calendarCartCount || 0) === 0) {
                    scrollToCalendarOption();
                  }
                  onPurchaseCalendarCart?.();
                  return;
                }
                setShowOptionModal?.(true);
              } else {
                setShowRequestChat?.(true);
              }
            }}
          />
        )
      )}
    </>
  );
};
